import React from 'react';
import { get, isEmpty, findIndex, filter, forEach, merge, find, cloneDeep, isBoolean, omit, set } from 'lodash';
import { withRouter } from 'react-router-dom';
import shortid from 'shortid';
import queryString from 'query-string';

// React view component to render
import { epostInsuranceInformation as EpostInsuranceInformationView } from 'ui/views/widgets/epostInsuranceInformation';
import { EpostInsuranceInformationService } from 'services/epostInsuranceInformation';
import { SERVICE_RESPONSE_STATUS } from 'constants/common';
import * as pageLevelPreloader from 'modules/page-level-preloader';
import * as errorOverlay from 'utils/errorOverlay';
import { getLabel } from 'utils/getLabel';
import { DASHBOARD_SUB_ROUTES } from 'constants/route-paths';
import { messageBus as MessageBus } from 'core/modules/message-bus';
import * as MESSAGE_BUS_EVENTS from 'constants/message-bus-events';

/**
 * @type {class}
 * @desc A React container/smart component to render a React presentational/dumb component (view).
 * @example
 * import { EpostInsuranceInformation } from 'ui/containers/EpostInsuranceInformation';
 *
 * // render
 * <EpostInsuranceInformation propName='propValue'/>
 */
class _EpostInsuranceInformation extends React.Component {
    
    /**
     * @param {object} props - props
     */
    constructor( props ) {
        super( props );
        
        // check if URL container `step` query parameter.
        const searchQuery = queryString.parse( this.props.location.search );

        // component state
        this.state = {
            isFetching: true,
            networkStatus: undefined,
            componentData: undefined,
            requestPayload: {},
            isBasicInfoFormValid: true,
            origin: get( searchQuery, 'origin', null )
        };

        // create service instance
        this.epostInsuranceInformationService = new EpostInsuranceInformationService();
        
        // start data fetch
        this.fetchComponentData();

        // bind
        this.__bind();
    }

    __bind() {
        this.handleNavActions = this.handleNavActions.bind( this );
        this.updateBasicInfo = this.updateBasicInfo.bind( this );
        this.handleDrugPreferenceSwitch = this.handleDrugPreferenceSwitch.bind( this );
        this.handleAddressInfoUpdate = this.handleAddressInfoUpdate.bind( this );
        this.handleDeleteAddressAction = this.handleDeleteAddressAction.bind( this );
        this.handleSetAddressDefaultAction = this.handleSetAddressDefaultAction.bind( this );
        this.handleAddEditCommunication = this.handleAddEditCommunication.bind( this );
        this.handleCommunicationDefaultSelection = this.handleCommunicationDefaultSelection.bind( this );
        this.handleCommunicationDetailDelete = this.handleCommunicationDetailDelete.bind( this );
        this.submitStepData = this.submitStepData.bind( this );
        this.handleAddressInfoUpdateBasedOnSection = this.handleAddressInfoUpdateBasedOnSection.bind( this );
        this.handleDeleteAddressActionBasedOnSection = this.handleDeleteAddressActionBasedOnSection.bind( this );
        this.handleSetAddressDefaultActionBasedOnSection = this.handleSetAddressDefaultActionBasedOnSection.bind( this );
        this.handleSwitchBillingAddressSameAsShipping = this.handleSwitchBillingAddressSameAsShipping.bind( this );
    }

    /**
     * handle nav actions
     */
    handleNavActions( { action } ) {
        log.debug( 'EpostInsuranceInformation.handleNavActions()' );

        if( 'cancel' === action.name ) {

            if( 'verification' === get( this.state, 'origin', null ) ) {

                this.props.history.push( {
                    pathname: DASHBOARD_SUB_ROUTES.CALL_AUTHORIZATION_FLOW,
                    search: queryString.stringify( {
                        verifyOther: true,
                        step: 2,
                    } ),
                } );

            } else {
                this.props.history.goBack();
            }
        }

        if( 'next' === action.name ) {
            
            const addresses = cloneDeep( get( this.state, 'componentData.shippingAddress.addresses', [] ) );
            const contacts = cloneDeep( get( this.state, 'componentData.contactInformation.contacts', {} ) );
            const billingAddress = cloneDeep( get( this.state, 'componentData.billingAddress', {} ) );

            forEach( addresses, address => {
                if( get( address, 'isTempId', false ) ) {
                    address.id = null;
                    address = omit( address, [ 'isTempId' ] );
                }
            } );

            forEach( contacts, contact => {
                if( get( contact, 'isTempId', false ) ) {
                    contact.id = null;
                    contact = omit( contact, [ 'isTempId' ] );
                }
            } );

            const data = {
                basicInformation: get( this.state, 'componentData.basicInformation', {} ),
                contacts: contacts,
                useGeneric: get( this.state, 'componentData.drugPreference.useGeneric' ),
                insurance: get( this.state, 'componentData.insurance.currentInsurance', {} ),
                addresses: addresses,
                billingAddress: billingAddress
            };
            this.submitStepData( { data } );
        }
    }

    /**
     * submit data
     */
    async submitStepData( { data } ) {
        log.debug( 'EpostInsuranceInformation.submitStepData()' );

        const action = find( this.state.componentData.navActions, { name: 'next' } );
        this.epostInsuranceInformationService.cancel();

        pageLevelPreloader.show();

        // get the request payload
        const requestPayload = cloneDeep( this.props.requestPayload );
        const dataPayLoad =  merge(
            requestPayload,
            { data }
        );

        const { status, data: _data } = await this.epostInsuranceInformationService.sumbitStepdata( {
            url: action.url,
            data: dataPayLoad,
            config: action.config
        } );

        pageLevelPreloader.hide();

        if( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {
            
            this.props.moveForward();

        } else {

            // clone data
            const __data = cloneDeep( _data );

            // set status in __data
            set( __data, 'status', status );

            // trigger success view with error data
            MessageBus.trigger( MESSAGE_BUS_EVENTS.SHOW_EPOST_JOURNEY_SUCCESS_VIEW, { data: __data } );
        }

    }

    /**
     * @desc Fetch component response.
     */
    async fetchComponentData() {
        log.debug( 'EpostInsuranceInformation.fetchComponentData()' );

        // cancel ongoing request
        this.epostInsuranceInformationService.cancel();

        // fetch component data
        const { status, data } = await this.epostInsuranceInformationService.fetchComponentData(  {
            url: this.props.widget.url,
            data: this.props.requestPayload
        } );

        // check if response status was success
        if( SERVICE_RESPONSE_STATUS.SUCCESS !== status ) {
            this.setState( {
                isFetching: false,
                networkStatus: SERVICE_RESPONSE_STATUS.FAILURE
            } );
        } else {
            this.setState( {
                isFetching: false,
                networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS,
                componentData: data
            } );
        }
    }

     /**
     * add/edit communication operation handler
     */
    handleAddEditCommunication( contact ) {
        log.debug( 'EpostInsuranceInformation.handleAddEditCommunication()' );

        const data = get( this.state, 'componentData', {} );

        if( ! isEmpty( contact.id ) ) {

            const contactIndex = findIndex( this.state.componentData.contactInformation.contacts, { id: contact.id } );
            data.contactInformation.contacts[ contactIndex ] = {
                ...data.contactInformation.contacts[ contactIndex ],
                ...contact
            };
        } else {

            const newContect = cloneDeep( contact );
            newContect.id = shortid.generate();
            newContect.isTempId = true;
            newContect.flags = {
                isDefault: false
            };
            newContect.actionsVisibility = {
                setDefault: true,
                edit: true,
                delete: true
            };

            data.contactInformation.contacts.push( newContect );
        }

        this.setState( { componentData: data } );
    }

    /**
     * default selection action handler
     */
    handleCommunicationDefaultSelection( { item } ) {
        log.debug( 'EpostInsuranceInformation.handleCommunicationDefaultSelection()' );

        const data = get( this.state, 'componentData', {} );
        const contactList = get( data, 'contactInformation.contacts' );

        forEach( contactList, contact => {
            contact.flags.isDefault = item.id === contact.id;
        } );
        
        data.contactInformation.contacts = contactList;
        this.setState( { componentData: data } );
    }

    /**
     * delete communication action handler
     */
    handleCommunicationDetailDelete( { item } ) {
        log.debug( 'EpostInsuranceInformation.handleCommunicationDetailDelete()' );

        const isDefault = get( item, 'flags.isDefault', false );
        const action = find( this.state.componentData.sections.contactInformation.modifyActions, { name: 'delete' } );

        if( ! isDefault ) {
            const data = get( this.state, 'componentData', {} );
            const newConactList = filter( data.contactInformation.contacts, _contact => {
                return _contact.id !== item.id;
            } );
    
            data.contactInformation.contacts = newConactList;
            this.setState( { componentData: data } );
        } else {

            errorOverlay.open( {
                message: getLabel( action.prohibit ),
                size: 'small',
                setDangerously: true
            } );
        }
    }

    /**
     * executed when user makes and update in the basic info form
     */
    updateBasicInfo( { name, dob, email, isValid } ) {
        log.debug( 'EpostInsuranceInformation.updateBasicInfo()' );

        if( ! isValid ) {
            return this.setState( { isBasicInfoFormValid: isValid } );
        }

        const data = get( this.state, 'componentData', {} );
        data.basicInformation.name = name;
        data.basicInformation.dob = dob;
        data.basicInformation.email = email;

        this.setState( {
            componentData: data,
            isBasicInfoFormValid: isValid
        } );
    }

    /**
     * switch drug prefernce action
     */
    handleDrugPreferenceSwitch( { value } ) {
        log.debug( 'EpostInsuranceInformation.handleDrugPreferenceSwitch()' );

        const data = get( this.state, 'componentData', {} );
        data.drugPreference.useGeneric = value;
        this.setState( {
            componentData: data
        } );
    }

     /**
     * add/edit opration handler
     */
    handleAddressInfoUpdate( { address, id = null } ) {
        log.debug( 'MemberAddEditAddress.handleAddressInfoUpdate()' );

        const data = get( this.state, 'componentData', {} );

        if( ! isEmpty( id ) ) {
            const addressIndex = findIndex( this.state.componentData.shippingAddress.addresses, { id: id } );

            data.shippingAddress.addresses[ addressIndex ] = {
                ...data.shippingAddress.addresses[ addressIndex ],
                ...address
            };

        } else {
            
            const newAdress = address;
            newAdress.id = shortid.generate();
            newAdress.isTempId = true;
            newAdress.flags = {
                isDefault: true
            };
            newAdress.actionsVisibility = {
                setDefault: false,
                edit: true,
                delete: false
            };
            data.shippingAddress.addresses.push( address );
        }

        this.setState( { componentData: data }, () => {

            // in case of edit only
            if ( !isEmpty( id ) ) {

                const billingData = get( this.state, 'componentData.billingAddress', {} );

                if ( !isEmpty( billingData ) && get( billingData, 'sameAsShipping', false ) ) {

                    this.handleSwitchBillingAddressSameAsShipping( { value: get( billingData, 'sameAsShipping', false ), isShippingUpdate: true } );
                }
            }
        } );
    }

     /**
     * add/edit opration handler based on section name passed from view
     */
      handleAddressInfoUpdateBasedOnSection( { address, id = null, sectionName = null } ) {
        log.debug( 'MemberAddEditAddress.handleAddressInfoUpdateBasedOnSection()' );

        const data = get( this.state, 'componentData', {} );

        if( ! isEmpty( id ) ) {
            const addressIndex = findIndex( this.state.componentData[ sectionName ].addresses, { id: id } );

            data[ sectionName ].addresses[ addressIndex ] = {
                ...data[ sectionName ].addresses[ addressIndex ],
                ...address
            };

        } else {
            
            const newAdress = address;
            newAdress.id = shortid.generate();
            newAdress.isTempId = true;
            newAdress.flags = {
                isDefault: true
            };
            newAdress.actionsVisibility = {
                setDefault: false,
                edit: true,
                delete: false
            };
            data[ sectionName ].addresses.push( address );
        }

        this.setState( { componentData: data } );

    }

    /**
     * delete address action
     */
    handleDeleteAddressAction( { address } ) {
        log.debug( 'MemberAddEditAddress.handleDeleteAddressAction()' );

        const isDefault = get( address, 'flags.isDefault', false );
        const action = find( this.state.componentData.sections.shippingAddress.modifyActions, { name: 'delete' } );

        if( ! isDefault ) {
            const data = get( this.state, 'componentData', {} );
            const newAddress = filter( data.shippingAddress.addresses, _address => {
                return _address.id !== address.id;
            } );
    
            data.shippingAddress.addresses = newAddress;
            this.setState( { componentData: data } );
        } else {
            
            errorOverlay.open( {
                message: getLabel( action.prohibit ),
                size: 'small',
                setDangerously: true
            } );
        }
    }

    /**
     * delete address action based on section name
     */
    handleDeleteAddressActionBasedOnSection( { address, sectionName = null } ) {
        log.debug( 'MemberAddEditAddress.handleDeleteAddressActionBasedOnSection()' );

        const isDefault = get( address, 'flags.isDefault', false );
        const action = find( this.state.componentData.sections[ sectionName ].modifyActions, { name: 'delete' } );

        if( ! isDefault ) {
            const data = get( this.state, 'componentData', {} );
            const newAddress = filter( data[ sectionName ].addresses, _address => {
                return _address.id !== address.id;
            } );

            data[ sectionName ].addresses = newAddress;
            this.setState( { componentData: data } );
        } else {
            
            errorOverlay.open( {
                message: getLabel( action.prohibit ),
                size: 'small',
                setDangerously: true
            } );
        }
    }

    /**
    * make the address default
    */
    handleSetAddressDefaultAction( { address } ) {
        log.debug( 'MemberAddEditAddress.handleSetAddressDefaultAction()' );

        const data = get( this.state, 'componentData', {} );
        const addressList = get( data, 'shippingAddress.addresses' );

        forEach( addressList, _address => {
            _address.flags.isDefault = address.id === _address.id;
        } );
        
        data.shippingAddress.addresses = addressList;
        this.setState( { componentData: data } );
    }


    /**
    * make the address default
    */
     handleSetAddressDefaultActionBasedOnSection( { address, sectionName = null } ) {
        log.debug( 'MemberAddEditAddress.handleSetAddressDefaultActionBasedOnSection()' );

        const data = get( this.state, 'componentData', {} );
        const addressList = get( data, `${sectionName}.addresses` );

        forEach( addressList, _address => {
            _address.flags.isDefault = address.id === _address.id;
        } );
        
        data[ sectionName ].addresses = addressList;
        this.setState( { componentData: data } );
    }

    /**
    * switch billing address same as shippping address
    */
     handleSwitchBillingAddressSameAsShipping( { value, isShippingUpdate = false } ) {
        log.debug( 'MemberAddEditAddress.handleSwitchBillingAddressSameAsShipping()' );

        const data = get( this.state, 'componentData.billingAddress', {} );

        // kept copy of billing initial address in case of switching back to selection no
        const billingInitialAddress = cloneDeep( get( data, 'addresses', [] ) );

        // If same value comes from view return from here
        if ( ( data.sameAsShipping === value ) && !isShippingUpdate ) {
            return;
        }

        data.sameAsShipping = value;

        if ( value ) {

            data.addresses = cloneDeep( get( this.state, 'componentData.shippingAddress.addresses', [] ) );
        } else {

            data.addresses = cloneDeep( get( this.state, 'componentData.billingInitialAddress', [] ) );
        }

        this.setState( {
            componentData: {
                ...this.state.componentData,
                billingAddress: data,
                billingInitialAddress: billingInitialAddress
            }
        } );

    }

    /*********************************************/

    /**
     * @desc When component unmounts, cleanup resouces.
     */
    componentWillUnmount() {
        this.epostInsuranceInformationService.cancel(); // cancel ongoing requests
    }

    /**
     * @desc Render a React view component
     */
    render() {

        // const basicInformation = get( this.state, 'componentData.basicInformation', {} );

		// const invalidInfo  = pickBy( basicInformation, ( _value = {} ) => ( isEmpty( _value ) ) );
        const contacts = get( this.state, 'componentData.contactInformation.contacts', [] );
		const addresses = get( this.state, 'componentData.shippingAddress.addresses', [] );
		const drugPreference =  get( this.state, 'componentData.drugPreference.useGeneric', undefined );
        const billingAddresses = get( this.state, 'componentData.billingAddress.addresses', [] );
		let canMoveForward = false;

		if( ! isEmpty( contacts ) && ! isEmpty( addresses ) /* && isBoolean( drugPreference ) */ /* && isEmpty( invalidInfo ) */ && this.state.isBasicInfoFormValid  ) {
			canMoveForward = true;
        }

        return (
            <EpostInsuranceInformationView
                isFetching={ this.state.isFetching }
                networkStatus={ this.state.networkStatus }
                { ...get( this.state, 'componentData', {} ) }
                onNavActionClick={ this.handleNavActions }
                onBasicInfoUpdate = { this.updateBasicInfo }
                onSwitchDrugPrefernce={ this.handleDrugPreferenceSwitch }
                
                // address action handlers
                onAddEditAddressClick={ this.handleAddressInfoUpdate }
                onAddressDelete={ this.handleDeleteAddressAction }
                onAddressDefaultSelection={ this.handleSetAddressDefaultAction }
                onAddEditAddressClickBasedOnSection = { this.handleAddressInfoUpdateBasedOnSection }
                onAddressDeleteBasedOnSection = { this.handleDeleteAddressActionBasedOnSection }
                onAddressDefaultSelectionBasedOnSection = { this.handleSetAddressDefaultActionBasedOnSection }
                onSwitchBillingAddressSameAsShipping = { this.handleSwitchBillingAddressSameAsShipping }
               
                // communication action handlers
                onAddEditCommunicationDetail={ this.handleAddEditCommunication }
                onCommunicationDefaultSelection={ this.handleCommunicationDefaultSelection }
                onCommunicationDelete={ this.handleCommunicationDetailDelete }

                canMoveForward={ canMoveForward }
            />
        );
    }
}

// set display name
_EpostInsuranceInformation.displayName = 'EpostInsuranceInformation';

// set default props
_EpostInsuranceInformation.defaultProps = {};

// prop types
_EpostInsuranceInformation.propTypes = {};


export const EpostInsuranceInformation = withRouter( _EpostInsuranceInformation );
